"use client";

import { useState, useEffect } from "react";
import { usePreloadedQuery, Preloaded } from "convex/react";
import { api } from "@/convex/_generated/api";
import { PuckRenderer } from "@/components/PuckRenderer";

/**
 * Inner component that attaches to live Convex data.
 * This only runs on the client.
 */
function LiveContent({
    preloadedPageData,
    preloadedSettings,
    preloadedStats,
    preloadedCategories,
    preloadedProducts,
    preloadedBlogs,
    useDynamicData,
    fallbackData
}: any) {
    const livePage = usePreloadedQuery(preloadedPageData);
    const liveSettings = usePreloadedQuery(preloadedSettings);
    const liveStats = usePreloadedQuery(preloadedStats);
    if (preloadedBlogs) {
        return (
            <LiveContentWithBlogs
                livePage={livePage}
                liveSettings={liveSettings}
                liveStats={liveStats}
                preloadedBlogs={preloadedBlogs}
                preloadedCategories={preloadedCategories}
                preloadedProducts={preloadedProducts}
                useDynamicData={useDynamicData}
                fallbackData={fallbackData}
            />
        );
    }
    return preloadedCategories && preloadedProducts ? (
        <LiveContentWithCatalog
            livePage={livePage}
            liveSettings={liveSettings}
            liveStats={liveStats}
            preloadedCategories={preloadedCategories}
            preloadedProducts={preloadedProducts}
            useDynamicData={useDynamicData}
            fallbackData={fallbackData}
        />
    ) : (
        <LiveRenderer
            livePage={livePage}
            liveSettings={liveSettings}
            liveStats={liveStats}
            liveCategories={null}
            liveProducts={null}
            useDynamicData={useDynamicData}
            fallbackData={fallbackData}
        />
    );
}

function LiveContentWithBlogs({ preloadedBlogs, ...props }: any) {
    const liveBlogs = usePreloadedQuery(preloadedBlogs);
    if (props.preloadedCategories && props.preloadedProducts) {
        return <LiveContentWithCatalog {...props} liveBlogs={liveBlogs} />;
    }
    return (
        <LiveRenderer
            {...props}
            liveCategories={null}
            liveProducts={null}
            liveBlogs={liveBlogs}
        />
    );
}

function LiveContentWithCatalog({
    preloadedCategories,
    preloadedProducts,
    ...props
}: any) {
    const liveCategories = usePreloadedQuery(preloadedCategories);
    const liveProducts = usePreloadedQuery(preloadedProducts);
    return <LiveRenderer {...props} liveCategories={liveCategories} liveProducts={liveProducts} />;
}

function LiveRenderer({
    livePage,
    liveSettings,
    liveStats,
    liveCategories,
    liveProducts,
    useDynamicData,
    fallbackData,
    liveBlogs,
}: any) {

    let displayData = null;
    if (livePage?.data) {
        try {
            const parsed = typeof livePage.data === "string" ? JSON.parse(livePage.data) : livePage.data;
            if (parsed?.content?.length > 0) displayData = parsed;
        } catch (e) {
            console.error("Failed to parse CMS page data", e);
        }
    }

    if (!displayData && fallbackData) {
        displayData = fallbackData;
    }

    return (
        <PuckRenderer
            data={displayData || { root: {}, content: [] }}
            initialData={{
                initialDbCategories: liveCategories,
                initialDbProducts: liveProducts,
                globalStats: liveStats,
                useDynamicData: useDynamicData ?? true,
                initialBlogs: liveBlogs,
            }}
            siteSettings={liveSettings}
        />
    );
}

export function CmsPageClient({
    fallbackData,
    path,
    useDynamicData,
    preloadedPageData,
    preloadedSettings,
    preloadedStats,
    preloadedCategories,
    preloadedProducts,
    preloadedBlogs,
    initialPage,
    initialSettings,
    initialStats,
    initialCategories,
    initialProducts,
    initialBlogs,
}: {
    fallbackData: any;
    path: string;
    useDynamicData?: boolean;
    preloadedPageData: Preloaded<typeof api.pages.getPublishedPage>;
    preloadedSettings: Preloaded<typeof api.siteSettings.getPublic>;
    preloadedStats: Preloaded<typeof api.siteData.getStats>;
    preloadedCategories?: Preloaded<typeof api.categories.list>;
    preloadedProducts?: Preloaded<typeof api.products.listAll>;
    preloadedBlogs?: Preloaded<typeof api.blogs.listBlogs>;
    initialPage?: any;
    initialSettings?: any;
    initialStats?: any;
    initialCategories?: any;
    initialProducts?: any;
    initialBlogs?: any;
}) {
    const [mounted, setMounted] = useState(false);
    useEffect(() => setMounted(true), []);

    // 1. Server-side render (or first client pass): Use static data
    if (!mounted) {
        let displayData = null;
        if (initialPage?.data) {
            try {
                const parsed = typeof initialPage.data === "string" ? JSON.parse(initialPage.data) : initialPage.data;
                if (parsed?.content?.length > 0) displayData = parsed;
            } catch (e) {}
        }

        if (!displayData && fallbackData) displayData = fallbackData;

        return (
            <div className="min-h-screen bg-background">
                <PuckRenderer
                    data={displayData || { root: {}, content: [] }}
                    initialData={{
                        initialDbCategories: initialCategories,
                        initialDbProducts: initialProducts,
                        globalStats: initialStats,
                        useDynamicData: useDynamicData ?? true,
                        initialBlogs,
                    }}
                    siteSettings={initialSettings}
                />
            </div>
        );
    }

    // 2. Client-side: Attach to live Convex stream
    return (
        <div className="min-h-screen bg-background">
            <LiveContent
                preloadedPageData={preloadedPageData}
                preloadedSettings={preloadedSettings}
                preloadedStats={preloadedStats}
                preloadedCategories={preloadedCategories}
                preloadedProducts={preloadedProducts}
                preloadedBlogs={preloadedBlogs}
                useDynamicData={useDynamicData}
                fallbackData={fallbackData}
            />
        </div>
    );
}
